"use client";

import { twMerge } from "tailwind-merge";

type AllowedTags = "div" | "p" | "span" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
type FontWeight = "normal" | "medium" | "thin" | "semibold" | "bold";
type Color = keyof typeof COLORS;

export const STYLES = {
  h1: "text-xl md:text-2xl",
  h2: "text-lg md:text-xl",
  h3: "text-base md:text-lg",
  h4: "text-sm md:text-base",
  div: "text-xs md:text-sm",
  p: "text-xs md:text-sm",
  span: "text-2xs md:text-xs",
} as const;

export const COLORS = {
  primary: "text-primary",
  secondary: "text-secondary",
  gray: "text-gray-800",
  lightGray: "text-gray-500",
  white: "text-white",
  black: "text-black",
  warning: "text-yellow-500",
  red: "text-red-500",
  redDark: "text-red-800",
} as const;

type TypographyProps = {
  variant?: keyof typeof STYLES;
  className?: string;
  children: React.ReactNode;
  fontWeight?: FontWeight;
  color?: Color;
  as?: AllowedTags; // Allows overriding the rendered tag
  style?: React.CSSProperties;
  onClick?: () => void;
};

const fontWeights: Record<FontWeight, string> = {
  thin: "font-light", // 300
  normal: "font-normal", // 400
  medium: "font-medium", // 500
  semibold: "font-semibold", // 600
  bold: "font-bold", // 700
} as const;

const DEFAULT_VARIANT = "div";
const DEFAULT_COLOR = "primary";
const DEFAULT_FONT_WEIGHT = "normal";

/**
 * A versatile Typography component for rendering text with various styles and semantics.
 *
 * @param {TypographyProps} props - The properties for the Typography component.
 * @param {string} [props.variant=DEFAULT_VARIANT] - The variant of the typography, e.g., 'h1', 'p', etc.
 * @param {string} [props.className] - Additional CSS classes to apply to the component.
 * @param {React.ReactNode} props.children - The content to be rendered inside the typography component.
 * @param {string} [props.fontWeight=DEFAULT_FONT_WEIGHT] - The font weight to apply, e.g., 'bold', 'normal', etc.
 * @param {string} [props.color=DEFAULT_COLOR] - The color of the text, e.g., 'primary', 'secondary', etc.
 * @param {React.ElementType} [props.as] - The HTML element or React component to render as the root element.
 */
const Text = ({
  variant = DEFAULT_VARIANT,
  className,
  children,
  fontWeight = DEFAULT_FONT_WEIGHT,
  color = DEFAULT_COLOR,
  as,
  style,
  onClick,
}: TypographyProps) => {
  const Component = as || variant;
  const isHeading = typeof variant === "string" && variant.startsWith("h");
  const weightClass = isHeading ? "font-bold" : fontWeights[fontWeight];
  const colorClass = COLORS[color] || COLORS.primary;
  return (
    <Component
      className={twMerge(STYLES[variant], weightClass, colorClass, className)}
      data-testid="Text"
      style={style}
      onClick={onClick}
    >
      {children}
    </Component>
  );
};

export default Text;
